import {useCallback} from "react";
import {useBalanceStore} from "../store/useBalance.store";
import useCurrencyStore from "../store/useCurrency.store";
import {useProfileOnboardingStore} from "../store/useProfileOnboarding.store";
import useGetUserInfo from "./useGetUserInfo";
import useGetAccountPagoMovil from "./wallet/useGetAccountPagoMovil";

/**
 * Hook to check if user needs to register cedula and phone number for pago movil
 * @returns {Object} Object containing needsRegistration boolean and triggerModal function
 */
const usePagoMovilRegistration = () => {
  const {data: userData, isLoading, isSuccess, refetch: refetchUserData} = useGetUserInfo();
  const {data: pmAccounts, isSuccess: pmSuccess, refetch: refetchPmAccounts} = useGetAccountPagoMovil();
  const {selectedCurrency} = useCurrencyStore();
  const handleAddPagoMovilModal = useBalanceStore(state => state.handleAddPagoMovilModal);
  const handleWithdrawalModal = useBalanceStore(state => state.handleWithdrawalModal);
  const handleBankAccountModal = useBalanceStore(state => state.handleBankAccountModal);
  const setPendingAction = useBalanceStore(state => state.setPendingAction);
  const clearPendingAction = useBalanceStore(state => state.clearPendingAction);
  const pendingAction = useBalanceStore(state => state.pendingAction);
  const requestOnboardingOpen = useProfileOnboardingStore(state => state.requestOpen);

  const isEmpty = val => val === undefined || val === null || val === "" || val === "null";

  const isPagoMovilApplicable = !selectedCurrency?.id || selectedCurrency.id === "VES";

  const profileIncomplete =
    isSuccess &&
    pmSuccess &&
    (isEmpty(userData?.nationalID) || isEmpty(userData?.phoneNumber) || (pmAccounts?.length ?? 0) === 0);

  const needsRegistration = isPagoMovilApplicable && profileIncomplete;

  const triggerRegistrationModalIfNeeded = async (actionType = "deposit", forceRefresh = true) => {
    if (!isPagoMovilApplicable) {
      return false;
    }

    if (isLoading || !isSuccess) {
      return false;
    }

    let currentUserData = userData;
    let currentPmAccounts = pmAccounts;

    if (forceRefresh) {
      try {
        const [refreshUser, refreshPm] = await Promise.all([refetchUserData(), refetchPmAccounts()]);
        currentUserData = refreshUser?.data?.data?.data ?? userData;
        currentPmAccounts = refreshPm?.data ?? pmAccounts;
      } catch (_error) {
        currentUserData = userData;
        currentPmAccounts = pmAccounts;
      }
    }

    const needsRegistrationCheck =
      isEmpty(currentUserData?.nationalID) ||
      isEmpty(currentUserData?.phoneNumber) ||
      (currentPmAccounts?.length ?? 0) === 0;

    if (needsRegistrationCheck) {
      setPendingAction(actionType);
      requestOnboardingOpen();
      return true;
    }

    return false;
  };

  const executePendingAction = useCallback(async () => {
    try {
      const [refreshUser, refreshPm] = await Promise.all([refetchUserData(), refetchPmAccounts()]);
      const freshUserData = refreshUser?.data?.data?.data;
      const freshPmAccounts = refreshPm?.data ?? [];

      const stillNeedsRegistration =
        isEmpty(freshUserData?.nationalID) || isEmpty(freshUserData?.phoneNumber) || freshPmAccounts.length === 0;

      if (stillNeedsRegistration) {
        clearPendingAction();
        return;
      }

      if (pendingAction === "deposit") {
        clearPendingAction();
        handleBankAccountModal(true);
      } else if (pendingAction === "withdrawal") {
        clearPendingAction();
        handleWithdrawalModal();
      } else {
        clearPendingAction();
      }
    } catch (_error) {
      clearPendingAction();
    }
  }, [
    clearPendingAction,
    handleBankAccountModal,
    handleWithdrawalModal,
    pendingAction,
    refetchPmAccounts,
    refetchUserData,
  ]);

  return {
    needsRegistration,
    triggerRegistrationModalIfNeeded,
    executePendingAction,
    isUserDataComplete: () => !needsRegistration,
    hasNationalID: !!userData?.nationalID,
    hasPhoneNumber: !!userData?.phoneNumber,
    hasPagoMovilAccount: (pmAccounts?.length ?? 0) > 0,
    userData,
    isLoading,
    isDataLoaded: isSuccess && pmSuccess,
    handleAddPagoMovilModal,
  };
};

export default usePagoMovilRegistration;
